import { useState, useEffect } from 'react';
import './PurchaseConfirmation.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import FidgetLoader from './FidgetLoader';

function PurchaseConfirmation() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!sessionId) {
            navigate('/', { replace: true });
            return;
        }
        const fetchData = async () => {
            setLoading(true);
            setError('');
            try {
                const prodBaseUrl = process.env.NODE_ENV === 'production' ? 'https://api.mrreedsteamlab.com' : '';
                const url = `${prodBaseUrl}/api/purchase-confirmation?session_id=${sessionId}`;
                const res = await fetch(url);
                if (!res.ok) {
                    await res.text(); // consume body
                    throw new Error('Could not fetch purchase details.');
                }
                const json = await res.json();
                setData(json);
            } catch (err) {
                setError(err.message || 'An error occurred.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [sessionId, navigate]);

    if (loading) {
        return <div className="purchase-confirmation__container"><FidgetLoader width="60" height="60" /></div>;
    }
    if (error) {
        return <div className="purchase-confirmation__container"><div className="purchase-confirmation__error purchase-confirmation__error--blue">{error}</div></div>;
    }
    if (!data) {
        return null;
    }

    const {
        code, invoice, expiresAt, purchaseDate,
    } = data;
    const formattedDate = purchaseDate ? new Date(purchaseDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
    const formattedExpires = expiresAt ? new Date(expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

    const handleCopy = () => {
        if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        }
    };

    return (
        <div className="purchase-confirmation__bg themed-bg">
            <div className="purchase-confirmation__card themed-card">
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <h2 className="purchase-confirmation__title themed-title">Thank You for Your Purchase!</h2>
                </div>
                <div className="purchase-confirmation__date themed-date" style={{ textAlign: 'right' }}>{formattedDate}</div>
                <p className="purchase-confirmation__label themed-label">Your STEAM Lab access code is:</p>
                <div className="purchase-confirmation__code themed-code">
                    <span className="code-text">{code}</span>
                    <span
                        className="copy-btn copy-icon"
                        onClick={handleCopy}
                        title="Copy code to clipboard"
                        aria-label="Copy code to clipboard"
                        tabIndex={0}
                        role="button"
                        style={{ marginLeft: 16, position: 'relative' }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopy(); }}>
                        <FontAwesomeIcon icon={faCopy} color="white" size="1x" />
                        {copied && (
                            <span className="copy-tooltip">Copied!</span>
                        )}
                    </span>
                </div>
                <div className="purchase-confirmation__instructions themed-instructions">
                    <span>To gain access to the Members content, please follow these steps:</span>
                    <ol style={{ marginTop: 12, marginBottom: 12, paddingLeft: 24 }}>
                        <li>
                            Copy the above Access Code.
                            You can also find the code in the receipt that has been emailed to you.
                        </li>
                        <li>
                            <a href="/members" className="themed-link">Create a user account or log in</a>
                            {' '}
                            if you already have an account. Choose the "User Login" option.
                        </li>
                        <li>
                            Once you are logged in, you will be asked to enter the above
                            Access Code to gain access to the Members content.
                        </li>
                    </ol>
                    <div style={{ marginTop: 8, fontStyle: 'italic', color: '#ffffffba' }}>
                        NOTE: You will not be asked to enter this code again,
                        unless your membership has expired.
                    </div>
                </div>
                <div className="purchase-confirmation__invoice themed-invoice">
                    <h3 className="themed-invoice-title">Purchase Details</h3>
                    <table>
                        <tbody>
                            <tr>
                                <td>Purchase Date:</td>
                                <td style={{ textAlign: 'right' }}>{formattedDate}</td>
                            </tr>
                            {invoice?.amount && (
                                <tr>
                                    <td>Amount:</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {invoice.amount}
                                        {' '}
                                        {invoice.currency || ''}
                                    </td>
                                </tr>
                            )}
                            {formattedExpires && (
                                <tr>
                                    <td>Membership Expires:</td>
                                    <td style={{ textAlign: 'right' }}>{formattedExpires}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <p className="purchase-confirmation__thanks themed-thanks">
                    Thank you,
                    <br />
                    Mr. Reed's STEAM Lab
                </p>
            </div>
        </div>
    );
}

export default PurchaseConfirmation;
